// src/pages/AcademicYearPage.js — Edit + Delete added
import React, { useState, useEffect } from "react";
import { yearAPI } from "../api/api";
import toast from "react-hot-toast";
import "./CommonPage.css";
import "./AcademicYearPage.css";

const BLANK = {
  year_label: "",
  start_date: "",
  end_date: "",
  is_current: false,
};

// ── small helpers ─────────────────────────────────────────────
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("kn-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

// ── Confirm-delete modal ──────────────────────────────────────
function DeleteConfirm({ year, onConfirm, onCancel, loading }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.75)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        style={{
          background:
            "linear-gradient(135deg,rgba(10,22,40,0.99),rgba(15,28,52,0.99))",
          border: "1px solid rgba(239,68,68,0.3)",
          borderRadius: "18px",
          padding: "1.8rem",
          maxWidth: "360px",
          width: "100%",
          textAlign: "center",
          boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
        }}
      >
        <div style={{ fontSize: "2.5rem", marginBottom: "0.8rem" }}>🗑️</div>
        <h3
          style={{
            fontFamily: "Noto Sans Kannada",
            fontSize: "1rem",
            fontWeight: 700,
            color: "#fff",
            marginBottom: "0.5rem",
          }}
        >
          ಶೈಕ್ಷಣಿಕ ವರ್ಷ ಅಳಿಸಿ
        </h3>
        <p
          style={{
            fontFamily: "Noto Sans Kannada",
            fontSize: "0.82rem",
            color: "rgba(255,255,255,0.55)",
            lineHeight: 1.6,
            marginBottom: "0.5rem",
          }}
        >
          <strong style={{ color: "#fde047" }}>{year?.year_label}</strong> ಅನ್ನು
          ಅಳಿಸುವಿರಾ?
        </p>
        <p
          style={{
            fontFamily: "Noto Sans Kannada",
            fontSize: "0.75rem",
            color: "rgba(248,113,113,0.8)",
            marginBottom: "1.5rem",
            lineHeight: 1.5,
          }}
        >
          ⚠️ ಈ ವರ್ಷಕ್ಕೆ ಯಾವ ಹಾಜರಾತಿ ದಾಖಲೆ ಇದ್ದರೆ ಅಳಿಸಲಾಗುವುದಿಲ್ಲ. ಪ್ರಸ್ತುತ ವರ್ಷ
          ಅಳಿಸಲು ಮೊದಲು ಬೇರೆ ವರ್ಷ "ಪ್ರಸ್ತುತ" ಮಾಡಿ.
        </p>
        <div style={{ display: "flex", gap: "0.8rem" }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1,
              padding: "0.65rem",
              borderRadius: "10px",
              background: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "rgba(255,255,255,0.7)",
              fontFamily: "Noto Sans Kannada",
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ರದ್ದು
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{
              flex: 1,
              padding: "0.65rem",
              borderRadius: "10px",
              background: "linear-gradient(135deg,#dc2626,#b91c1c)",
              border: "none",
              color: "#fff",
              fontFamily: "Noto Sans Kannada",
              fontSize: "0.85rem",
              fontWeight: 700,
              cursor: "pointer",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "..." : "🗑️ ಅಳಿಸಿ"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function AcademicYearPage() {
  const [years, setYears] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [editId, setEditId] = useState(null); // null = add, number = edit
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const load = () => {
    setFetching(true);
    yearAPI
      .list()
      .then((r) => setYears(r.data.data || []))
      .catch(() => toast.error("ಲೋಡ್ ಆಗಲಿಲ್ಲ"))
      .finally(() => setFetching(false));
  };
  useEffect(() => {
    load();
  }, []);

  // ── Open add form ──
  const openAdd = () => {
    setForm(BLANK);
    setEditId(null);
    setShowForm(true);
  };

  // ── Open edit form ──
  const openEdit = (y) => {
    setForm({
      year_label: y.year_label,
      start_date: y.start_date ? y.start_date.slice(0, 10) : "",
      end_date: y.end_date ? y.end_date.slice(0, 10) : "",
      is_current: y.is_current === 1,
    });
    setEditId(y.id);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditId(null);
    setForm(BLANK);
  };

  // ── Auto-fill year label + end date from start date ──
  const handleStartDate = (val) => {
    setForm((p) => {
      const yr = new Date(val).getFullYear();
      const label = p.year_label || `${yr}-${String(yr + 1).slice(2)}`;
      const endDate = p.end_date || `${yr + 1}-03-31`;
      return { ...p, start_date: val, year_label: label, end_date: endDate };
    });
  };

  // ── Validate ──
  const validate = () => {
    if (!form.year_label.trim()) {
      toast.error("ವರ್ಷ ಲೇಬಲ್ ನಮೂದಿಸಿ (e.g. 2025-26)");
      return false;
    }
    if (!form.start_date) {
      toast.error("ಪ್ರಾರಂಭ ದಿನಾಂಕ ಆಯ್ಕೆ ಮಾಡಿ");
      return false;
    }
    if (!form.end_date) {
      toast.error("ಅಂತ್ಯ ದಿನಾಂಕ ಆಯ್ಕೆ ಮಾಡಿ");
      return false;
    }
    if (form.start_date >= form.end_date) {
      toast.error("ಅಂತ್ಯ ದಿನಾಂಕ ಪ್ರಾರಂಭ ದಿನಾಂಕಕ್ಕಿಂತ ಹೆಚ್ಚಿರಬೇಕು");
      return false;
    }
    return true;
  };

  // ── Save (create or update) ──
  const save = async () => {
    if (!validate()) return;
    setLoading(true);
    const payload = { ...form, is_current: form.is_current ? 1 : 0 };
    try {
      if (editId) {
        await yearAPI.update(editId, payload);
        toast.success("ಶೈಕ್ಷಣಿಕ ವರ್ಷ ಅಪ್‌ಡೇಟ್ ಆಗಿದೆ ✅");
      } else {
        await yearAPI.create(payload);
        toast.success("ಶೈಕ್ಷಣಿಕ ವರ್ಷ ಮಾಡಲಾಗಿದೆ ✅");
      }
      closeForm();
      load();
    } catch (e) {
      const msg = e.response?.data?.message || "ತಪ್ಪಾಗಿದೆ";
      toast.error(
        msg.includes("ಈಗಾಗಲೇ") || msg.includes("Duplicate")
          ? "ಈ ವರ್ಷ ಲೇಬಲ್ ಈಗಾಗಲೇ ಇದೆ"
          : msg,
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Delete ──
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setLoading(true);
    try {
      await yearAPI.remove(deleteTarget.id);
      toast.success(`${deleteTarget.year_label} ಅಳಿಸಲಾಗಿದೆ`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      const msg = e.response?.data?.message || "ಅಳಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ";
      toast.error(msg);
      setDeleteTarget(null);
    } finally {
      setLoading(false);
    }
  };

  const currentYear = years.find((y) => y.is_current === 1);

  return (
    <div className="common-page ay-page">
      <div className="page-header">
        <h1 className="page-title">📅 ಶೈಕ್ಷಣಿಕ ವರ್ಷ ನಿರ್ವಹಣೆ</h1>
        <p className="page-desc">
          ವರ್ಷ ಮಾಡಿ, ಸಂಪಾದಿಸಿ, ಅಳಿಸಿ — ಹಾಜರಾತಿ ಮತ್ತು ವರದಿಗಳಲ್ಲಿ ತಾನೇ ಬರುತ್ತದೆ
        </p>
      </div>

      {/* Current year banner */}
      {currentYear && (
        <div className="current-year-banner">
          <div className="cy-left">
            <span className="cy-icon">🎓</span>
            <div>
              <div className="cy-label">ಪ್ರಸ್ತುತ ಶೈಕ್ಷಣಿಕ ವರ್ಷ</div>
              <div className="cy-year">{currentYear.year_label}</div>
              <div className="cy-dates">
                {fmtDate(currentYear.start_date)} —{" "}
                {fmtDate(currentYear.end_date)}
              </div>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.6rem",
              flexWrap: "wrap",
            }}
          >
            <div className="cy-badge">✅ ಚಾಲ್ತಿಯಲ್ಲಿದೆ</div>
            <button
              className="ay-edit-small-btn"
              onClick={() => openEdit(currentYear)}
            >
              ✏️ ಸಂಪಾದಿಸಿ
            </button>
          </div>
        </div>
      )}

      {!currentYear && !fetching && (
        <div className="no-year-warning">
          ⚠️ ಯಾವ ಪ್ರಸ್ತುತ ಶೈಕ್ಷಣಿಕ ವರ್ಷ ನಿಗದಿ ಮಾಡಿಲ್ಲ! ಹಾಜರಾತಿ ನಮೂದಿಸಲು ಮೊದಲು
          ವರ್ಷ ಮಾಡಿ.
        </div>
      )}

      {/* Info cards */}
      <div className="ay-info-row">
        {[
          {
            icon: "✅",
            title: "ಹಾಜರಾತಿ",
            desc: "ಪ್ರಸ್ತುತ ವರ್ಷ ತಾನೇ ಆಯ್ಕೆ ಆಗುತ್ತದೆ",
          },
          { icon: "📋", title: "ವರದಿ", desc: "ವರ್ಷ ಪ್ರಕಾರ ಫಿಲ್ಟರ್ ಮಾಡಬಹುದು" },
          {
            icon: "🌾",
            title: "ಧಾನ್ಯ ಸ್ಟಾಕ್",
            desc: "ವರ್ಷವಾರು ಸ್ಟಾಕ್ ಟ್ರ್ಯಾಕ್",
          },
          {
            icon: "🔄",
            title: "ವರ್ಷ ಬದಲಿಸಿ",
            desc: '"ಪ್ರಸ್ತುತ" ಮಾಡಿದರೆ ಎಲ್ಲ ಕಡೆ ಬದಲಾಗುತ್ತದೆ',
          },
        ].map((c) => (
          <div key={c.title} className="ay-info-card">
            <span className="aic-icon">{c.icon}</span>
            <div className="aic-text">
              <div className="aic-title">{c.title}</div>
              <div className="aic-desc">{c.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Action bar */}
      <div className="action-bar">
        <button className="act-btn act-green" onClick={openAdd}>
          + ಹೊಸ ಶೈಕ್ಷಣಿಕ ವರ್ಷ ಮಾಡಿ
        </button>
      </div>

      {/* Add / Edit form */}
      {showForm && (
        <div className="ay-form-card">
          <div className="ay-form-header">
            <h3>
              {editId ? "✏️ ಶೈಕ್ಷಣಿಕ ವರ್ಷ ಸಂಪಾದಿಸಿ" : "ಹೊಸ ಶೈಕ್ಷಣಿಕ ವರ್ಷ"}
            </h3>
            <button className="modal-close" onClick={closeForm}>
              ✕
            </button>
          </div>

          {editId && (
            <div
              style={{
                background: "rgba(251,191,36,0.08)",
                border: "1px solid rgba(251,191,36,0.2)",
                borderRadius: "8px",
                padding: "0.6rem 0.9rem",
                marginBottom: "0.8rem",
                fontFamily: "Noto Sans Kannada",
                fontSize: "0.78rem",
                color: "#fde047",
              }}
            >
              ✏️ ಸಂಪಾದಿಸುತ್ತಿರುವುದು:{" "}
              <strong>{years.find((y) => y.id === editId)?.year_label}</strong>
            </div>
          )}

          <div className="ay-form-grid">
            <div className="form-row">
              <label className="form-row-label">
                ವರ್ಷ ಲೇಬಲ್ * <span className="hint">(e.g. 2025-26)</span>
              </label>
              <input
                className="modal-input"
                placeholder="2025-26"
                value={form.year_label}
                onChange={(e) =>
                  setForm((p) => ({ ...p, year_label: e.target.value }))
                }
              />
            </div>
            <div className="form-row">
              <label className="form-row-label">ಪ್ರಾರಂಭ ದಿನಾಂಕ *</label>
              <input
                className="modal-input"
                type="date"
                value={form.start_date}
                onChange={(e) => handleStartDate(e.target.value)}
              />
            </div>
            <div className="form-row">
              <label className="form-row-label">ಅಂತ್ಯ ದಿನಾಂಕ *</label>
              <input
                className="modal-input"
                type="date"
                value={form.end_date}
                onChange={(e) =>
                  setForm((p) => ({ ...p, end_date: e.target.value }))
                }
              />
            </div>
            <div className="form-row ay-current-toggle">
              <label className="form-row-label">
                ಪ್ರಸ್ತುತ ವರ್ಷ ಆಗಿ ಹೊಂದಿಸಿ
              </label>
              <label className="meal-toggle">
                <input
                  type="checkbox"
                  checked={form.is_current}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, is_current: e.target.checked }))
                  }
                />
                <span className="toggle-track">
                  <span className="toggle-thumb" />
                </span>
                <span
                  className="toggle-lbl"
                  style={{
                    fontFamily: "Noto Sans Kannada",
                    fontSize: "0.82rem",
                    color: form.is_current
                      ? "#4ade80"
                      : "rgba(255,255,255,0.5)",
                  }}
                >
                  {form.is_current
                    ? "✅ ಹೌದು — ಹಾಜರಾತಿ ಮತ್ತು ವರದಿಗಳಲ್ಲಿ ಈ ವರ್ಷ ಬರುತ್ತದೆ"
                    : "ಇಲ್ಲ"}
                </span>
              </label>
              {form.is_current && (
                <div className="ay-current-note">
                  ⚠️ ಹಳೆಯ ಪ್ರಸ್ತುತ ವರ್ಷ ತಾನೇ ನಿಷ್ಕ್ರಿಯ ಆಗುತ್ತದೆ
                </div>
              )}
            </div>
          </div>

          <div className="ay-form-actions">
            <button className="modal-cancel-btn" onClick={closeForm}>
              ರದ್ದು
            </button>
            <button
              className="modal-save-btn"
              onClick={save}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-sm" />
                  ಉಳಿಸುತ್ತಿದೆ...
                </>
              ) : editId ? (
                "💾 ಅಪ್‌ಡೇಟ್ ಮಾಡಿ"
              ) : (
                "💾 ಶೈಕ್ಷಣಿಕ ವರ್ಷ ಉಳಿಸಿ"
              )}
            </button>
          </div>
        </div>
      )}

      {/* Years list */}
      <div className="section-title-row">
        <h2 className="section-heading">
          ಎಲ್ಲಾ ಶೈಕ್ಷಣಿಕ ವರ್ಷಗಳು ({years.length})
        </h2>
      </div>

      {fetching ? (
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            color: "rgba(255,255,255,0.4)",
            fontFamily: "Noto Sans Kannada",
          }}
        >
          ಲೋಡ್ ಆಗುತ್ತಿದೆ...
        </div>
      ) : years.length === 0 ? (
        <div className="ay-empty">
          <div className="ay-empty-icon">📅</div>
          <div className="ay-empty-text">ಇನ್ನೂ ಯಾವ ಶೈಕ್ಷಣಿಕ ವರ್ಷ ಮಾಡಿಲ್ಲ</div>
          <div className="ay-empty-sub">
            ಮೇಲೆ "+ ಹೊಸ ಶೈಕ್ಷಣಿಕ ವರ್ಷ ಮಾಡಿ" ಬಟನ್ ಒತ್ತಿ
          </div>
        </div>
      ) : (
        <div className="ay-years-list">
          {years.map((y) => (
            <div
              key={y.id}
              className={`ay-year-card ${y.is_current === 1 ? "ay-current" : ""}`}
            >
              <div className="ayc-left">
                <div className="ayc-year-label">{y.year_label}</div>
                <div className="ayc-dates">
                  <span>📅 {fmtDate(y.start_date)}</span>
                  <span className="date-sep">→</span>
                  <span>{fmtDate(y.end_date)}</span>
                </div>
              </div>

              <div
                className="ayc-right"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  flexWrap: "wrap",
                }}
              >
                {y.is_current === 1 ? (
                  <span className="ay-status-badge current">✅ ಪ್ರಸ್ತುತ</span>
                ) : (
                  <span className="ay-status-badge past">📁 ಹಳೆಯದು</span>
                )}

                {/* Edit button */}
                <button
                  className="ay-action-btn ay-btn-edit"
                  onClick={() => openEdit(y)}
                  title="ಸಂಪಾದಿಸಿ"
                >
                  ✏️
                </button>

                {/* Delete button — disabled for current year */}
                <button
                  className={`ay-action-btn ay-btn-del ${y.is_current === 1 ? "ay-btn-disabled" : ""}`}
                  onClick={() => y.is_current !== 1 && setDeleteTarget(y)}
                  title={
                    y.is_current === 1
                      ? "ಪ್ರಸ್ತುತ ವರ್ಷ ಅಳಿಸಲು ಸಾಧ್ಯವಿಲ್ಲ"
                      : "ಅಳಿಸಿ"
                  }
                  disabled={y.is_current === 1}
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Usage guide */}
      <div className="ay-guide-card">
        <h3 className="ay-guide-title">ℹ️ ಹೇಗೆ ಬಳಸುವುದು?</h3>
        <div className="ay-guide-steps">
          {[
            "+ ಹೊಸ ಶೈಕ್ಷಣಿಕ ವರ್ಷ ಒತ್ತಿ ವರ್ಷ ಮಾಡಿ",
            '"ಪ್ರಸ್ತುತ ವರ್ಷ" toggle ON ಮಾಡಿ — ಹಾಜರಾತಿ ಮತ್ತು ವರದಿಗಳಲ್ಲಿ ತಾನೇ ಬರುತ್ತದೆ',
            "✏️ ಒತ್ತಿ label/date ತಿದ್ದಬಹುದು — ತಪ್ಪಾಗಿ ನಮೂದಿಸಿದ್ದರೆ ಇಲ್ಲಿ ಸರಿ ಮಾಡಿ",
            "🗑️ ಒತ್ತಿ ತಪ್ಪು ವರ್ಷ ಅಳಿಸಬಹುದು — ಹಾಜರಾತಿ ದಾಖಲೆ ಇದ್ದರೆ ಅಳಿಸಲಾಗುವುದಿಲ್ಲ",
            'ಪ್ರಸ್ತುತ ವರ್ಷ ಅಳಿಸಲು ಮೊದಲು ಬೇರೆ ವರ್ಷ "ಪ್ರಸ್ತುತ" ಮಾಡಿ',
          ].map((text, i) => (
            <div key={i} className="guide-step">
              <span className="step-num">{i + 1}</span>
              <span className="step-text">{text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <DeleteConfirm
          year={deleteTarget}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={loading}
        />
      )}
    </div>
  );
}
